
  import { defineComponent, PropType } from 'vue';
  import { Cell, createColumnHelper } from '@tanstack/vue-table';

  import TableAggregated from '@/common/components/redesigned/Table/TableAggregated.vue';
  import {
    TPaginationConfig,
    TTableAggregatedProgressBarOptions,
  } from '@/common/components/redesigned/Table/types/tableTypes';
  import text from '@/common/mixins/text';

  import { PAGINATION_CONFIG } from '../../constants/pagination';
  import { TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS } from '../../constants/tableAggregatedProgress';
  import {
    FOREIGN_TRADE_BY_COUNTRIES_ACTIONS_NAME,
    FOREIGN_TRADE_BY_COUNTRIES_GETTERS_NAME,
    FOREIGN_TRADE_BY_COUNTRIES_MUTATIONS_NAME,
  } from '../../store/foreignTradeByCountries/name';
  import { IndicatorTypes } from '../../types/common-aggregated';
  import {
    TForeignTradeByCountriesFilter,
    TForeignTradeByCountriesItem,
    TForeignTradeByCountriesTableData,
  } from '../../types/foreign-trade-by-countries';

  const CELL_LEVEL_CLASS = 'foreign-trade-by-countries-table__cell--level';

  export default defineComponent({
    name: 'ForeignTradeByCountriesTable',
    components: {
      TableAggregated,
    },
    mixins: [text],
    props: {
      filter: {
        type: Object as PropType<TForeignTradeByCountriesFilter>,
        required: true,
      },
    },
    data() {
      return {
        storePaths: {
          action: FOREIGN_TRADE_BY_COUNTRIES_ACTIONS_NAME.searchCountryAggregated,
          getter: FOREIGN_TRADE_BY_COUNTRIES_GETTERS_NAME.getCountryAggregated,
          mutation: FOREIGN_TRADE_BY_COUNTRIES_MUTATIONS_NAME.setCountryAggregated,
        },
      };
    },
    computed: {
      getPaginationConfig(): TPaginationConfig {
        return PAGINATION_CONFIG;
      },
      getProgressBarOptions(): TTableAggregatedProgressBarOptions {
        return TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS;
      },
    },
    methods: {
      generateColumns(data: TForeignTradeByCountriesTableData) {
        if (!data) return [];

        const columnHelper = createColumnHelper<TForeignTradeByCountriesItem>();

        return [
          // Поставил игнорирования из-за того, что в браузере вылезает ошибка типа (после обновления ts должна уйти)
          // @ts-ignore
          columnHelper.accessor('country', {
            header: 'Страна',
            size: 316,
            meta: {
              class: 'country-cell',
              fixed: true,
            },
          }),
          ...(data.subjectsRf ?? []).map((subjectRf: string, index: number) => {
            return columnHelper.group({
              header: subjectRf,
              id: `${subjectRf}${index}`,
              meta: {
                class: 'subject-rf-cell',
              },
              columns: [
                ...data.periods.map((period: string, periodIndex: number) => {
                  return columnHelper.group({
                    header: period,
                    id: `${period}${periodIndex}`,
                    columns: [
                      columnHelper.group({
                        header: 'Экспорт',
                        columns: this.getTradeColumns(columnHelper, periodIndex, 'export'),
                      }),
                      columnHelper.group({
                        header: 'Импорт',
                        columns: this.getTradeColumns(columnHelper, periodIndex, 'import'),
                      }),
                    ],
                  });
                }),
              ],
            });
          }),
        ];
      },
      getTradeColumns(columnHelper: any, periodIndex: number, tradeType: 'export' | 'import') {
        const columns = [];

        if (
          this.filter.indicatorTypeId === IndicatorTypes.WEIGHT ||
          this.filter.indicatorTypeId === IndicatorTypes.PRICE_AND_WEIGHT
        ) {
          columns.push(
            // @ts-ignore
            columnHelper.accessor(
              (row: TForeignTradeByCountriesItem) =>
                this.formatNumber(row.indicators[periodIndex]?.[tradeType].weight, 1),
              {
                header: 'Вес, тонн',
                id: `${tradeType}-weight-${periodIndex}`,
                size: 160,
              },
            ),
          );
        }

        if (
          this.filter.indicatorTypeId === IndicatorTypes.PRICE ||
          this.filter.indicatorTypeId === IndicatorTypes.PRICE_AND_WEIGHT
        ) {
          columns.push(
            // @ts-ignore
            columnHelper.accessor(
              (row: TForeignTradeByCountriesItem) =>
                this.formatNumber(row.indicators[periodIndex]?.[tradeType].price, 1),
              {
                header: 'Стоимость, тыс. долл.',
                id: `${tradeType}-price-${periodIndex}`,
                size: 160,
              },
            ),
          );
        }

        return columns;
      },
      cellClassGetter(cell: Cell<TForeignTradeByCountriesItem, unknown>) {
        if (cell.column.id !== 'country') {
          return '';
        }

        const { level } = cell.row.original;

        return `${CELL_LEVEL_CLASS} ${CELL_LEVEL_CLASS}-${level}`;
      },
    },
  });
